import {api, resolveOnStatus} from './index';
import {stripPlanTitle} from 'utils/planName';
import type {ActivationTier} from '@repo/types';

export type SubscriptionInterval = 'month' | 'year' | string;

export interface SubscriptionPlan {
  id: string;
  stripe_product_id: string;
  stripe_price_id: string;
  title: string;
  description?: string | null;
  features?: unknown;
  amount: number;
  interval: SubscriptionInterval;
  /** Activations included free per billing period before graduated metering starts. */
  included_activations?: number;
  /**
   * Seats this plan is sold with (VRG-438). `null` = UNLIMITED — and also what a
   * plan row predating the column, or one never synced from Stripe, carries.
   *
   * The conflation is deliberate and safe: the only thing this number does is
   * decide whether an invite is refused, and both readings answer that the same
   * way (do not block). Never substitute 0 for a missing value — that would read
   * as "no seats" and lock a brand out of its own Users page.
   */
  included_seats?: number | null;
  /**
   * The tier's STABLE identity (VRG-365) — `starter` | `individual` | `business`
   * | `enterprise`, mirrored from the Stripe product's `metadata.vera_catalog`.
   * Key all tier-specific UI on this, NEVER on `title`: titles are Stripe-authored
   * billing copy that carries a "Vera " prefix on invoices, which is exactly what
   * broke the plan-comparison matrix. Typed as the loose `string` the API can
   * actually return; narrow with `isPlanTierKey` when a canonical key is needed.
   * Null/absent on legacy rows that predate the column.
   */
  tier_key?: string | null;
  /**
   * Graduated activation-pricing schedule for this plan's metered price
   * (VRG-375), read straight from the API instead of a hardcoded client-side
   * array. `null` when the sync hasn't resolved tiers out of Stripe — callers
   * must render nothing rather than substitute a default (a wrong price is
   * worse than no price; see `hasActivationTiers`).
   */
  activation_tiers: ActivationTier[] | null;
  active: boolean;
  created_at?: string;
  updated_at?: string;
}

export type SubscriptionStatus =
  | 'active'
  | 'incomplete'
  | 'canceled'
  | 'unpaid'
  | 'past_due'
  | 'trialing'
  | string;

export interface CompanySubscription {
  id: string;
  company_id: string;
  stripe_subscription_id: string | null;
  stripe_product_id: string;
  status: SubscriptionStatus;
  current_period_start: string | null;
  current_period_end: string | null;
  canceled_at: string | null;
  trial_end: string | null;
  created_at: string;
  updated_at: string | null;
  metadata?: Record<string, unknown> | null;
  plan?: SubscriptionPlan | null;
}

export interface VerifySubscriptionResponse {
  subscription: CompanySubscription;
}

export const subscriptionApi = {
  async listPlans(): Promise<SubscriptionPlan[]> {
    const response = await api.get('/subscriptions/get-all-plans');
    // The signature promises an array; the server does not. When this endpoint fails
    // it answers 200-shaped `{message, code, details}`, which sailed straight through
    // to `plans.filter(...)` in CreateCompanyModal and took the whole internal
    // Companies page down behind the error boundary — a backend fault presenting as a
    // dead front end. `= []` defaults do not catch it: the value is an object, not
    // undefined. Enforce the declared type at the boundary instead.
    return Array.isArray(response.data)
      ? response.data.map(stripPlanTitle)
      : [];
  },

  async getCurrentCompanySubscription(): Promise<CompanySubscription | null> {
    // A company with no subscription is a normal state, so the 404 is caught rather than read
    // off a resolved response — `createApiCall` rejects on every >= 400 (VRG-386), which made
    // the old `if (response.status === 404)` line unreachable and pushed the Billing plan card
    // and the sidebar plan chip into an error state instead of their empty one.
    return resolveOnStatus(
      api.get(`/subscriptions/current`).then((response) => {
        // The subscription embeds its own plan, and that is the copy the Billing Plan card
        // and the sidebar chip both read — so it needs the same treatment as the catalog.
        const data = response.data;
        return data?.plan ? {...data, plan: stripPlanTitle(data.plan)} : data;
      }),
      [404],
      null
    );
  },

  async verifySubscription(
    sessionId: string,
  ): Promise<VerifySubscriptionResponse> {
    const response = await api.post('/billing/subscription/verify', {
      sessionId,
    });
    return response.data;
  },
};
